// src/ConsumerApp/Screens/Bucket/components/BucketItemCard.js

import React, { useState, useEffect } from 'react';

// Real SVGs from assets/icons
import Bookmark from '../../../../assets/icons/bookmark.svg';
import FillBookmark from '../../../../assets/icons/FillBookmark.svg';
import Trash from '../../../../assets/icons/Trash.svg';
import Plus from '../../../../assets/icons/WhitePlus.svg';
import Minus from '../../../../assets/icons/WhiteMinus.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function BucketItemCard({
  item,
  onUpdateQty,
  onRemove,
  isSavedItem,
  onToggleSave,
}) {
  const id = item.item_id || item.product_id || item._id;

  // Resilient text fallbacks for both guest & server data schemas
  const name = item.item_name || item.product_name || item.name || 'Material Item';
  const brand = item.brand || item.brand_name || '';
  const unitText = item.uom_name || item.UOM || item.uom || '';

  const quantity = Number(item.quantity) || 1;
  const minQty = Number(item.min_qty) || 1;
  const maxQty = Number(item.max_qty) > 0 ? Number(item.max_qty) : 999;

  const unitPrice =
    Number(item.price) ||
    Number(item.unit_price) ||
    Number(item.selling_price) ||
    0;
  const itemTotal = Number(item.item_total) || unitPrice * quantity;
  const itemMrpTotal =
    Number(item.item_mrp_total) ||
    (Number(item.mrp) > 0 ? Number(item.mrp) * quantity : 0);

  const [imgError, setImgError] = useState(false);
  const [inputValue, setInputValue] = useState(String(quantity));
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(Boolean(isSavedItem));

  useEffect(() => {
    setInputValue(String(quantity));
  }, [quantity]);

  useEffect(() => {
    setLocalIsSaved(Boolean(isSavedItem));
  }, [isSavedItem]);

  const handleIncrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) + 1;
    if (next <= maxQty) {
      setInputValue(String(next));
      if (onUpdateQty) onUpdateQty(id, next);
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) - 1;
    if (next < minQty) {
      setShowConfirmDelete(true);
    } else {
      setInputValue(String(next));
      if (onUpdateQty) onUpdateQty(id, next);
    }
  };

  const handleBlur = () => {
    let val = parseInt(inputValue, 10);
    if (isNaN(val) || val < 1) {
      setShowConfirmDelete(true);
      return;
    }
    if (val > maxQty) val = maxQty;
    setInputValue(String(val));
    if (val !== quantity && onUpdateQty) {
      onUpdateQty(id, val);
    }
  };

  // Safe Image URL resolution: avoids double-prefixing absolute URLs
  const rawImg =
    item.item_image ||
    item.product_image ||
    item.product_image_url ||
    item.image;

  const imageUrl = rawImg
    ? rawImg.startsWith('http://') ||
      rawImg.startsWith('https://') ||
      rawImg.startsWith('data:')
      ? rawImg
      : `${IMAGE_BASE_URL}${rawImg}`
    : null;

  return (
    <div className="bb-bucket-item-card">
      <style>{`
        .bb-bucket-item-card {
          background-color: #FFFFFF;
          border-bottom: 1px solid #F1F5F9;
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
          box-sizing: border-box;
          width: 100%;
          transition: background-color 0.15s ease;
        }

        .bb-bucket-item-card:last-child {
          border-bottom: none;
        }

        .bb-bic-top-tier {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          width: 100%;
        }

        .bb-bic-img-wrapper {
          width: 52px;
          height: 52px;
          border-radius: 10px;
          background-color: #F8FAFC;
          border: 1px solid #E2E8F0;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-shrink: 0;
          overflow: hidden;
          padding: 4px;
          box-sizing: border-box;
        }

        .bb-bic-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .bb-bic-meta-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .bb-bic-brand-tag {
          font-size: 10px;
          color: #64748B;
          text-transform: uppercase;
          font-weight: 800;
          letter-spacing: 0.3px;
        }

        .bb-bic-title {
          margin: 0;
          font-size: 13.5px;
          font-weight: 700;
          color: #0F172A;
          line-height: 17px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .bb-bic-unit-spec {
          font-size: 11.5px;
          color: #64748B;
          font-weight: 500;
        }

        .bb-bic-actions-cluster {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .bb-bic-action-icon-btn {
          width: 30px;
          height: 30px;
          border-radius: 15px;
          background-color: #F8FAFC;
          border: 1px solid #E2E8F0;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          padding: 0;
          transition: background-color 0.15s ease;
        }

        .bb-bic-action-icon-btn:hover {
          background-color: #F1F5F9;
        }

        .bb-bic-bottom-tier {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding-top: 2px;
        }

        .bb-bic-price-group {
          display: flex;
          align-items: baseline;
          gap: 6px;
          flex-wrap: wrap;
        }

        .bb-bic-final-price {
          font-size: 15px;
          font-weight: 800;
          color: #0F172A;
        }

        .bb-bic-mrp-price {
          font-size: 11.5px;
          color: #94A3B8;
          text-decoration: line-through;
        }

        .bb-bic-stepper-box {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #E9001E;
          border-radius: 8px;
          height: 32px;
          width: 90px;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(233, 0, 30, 0.25);
          flex-shrink: 0;
        }

        .bb-bic-stepper-btn {
          width: 28px;
          height: 100%;
          background: transparent;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.15s ease;
        }

        .bb-bic-stepper-btn:active {
          opacity: 0.75;
        }

        .bb-bic-stepper-input {
          flex: 1;
          width: 100%;
          border: none;
          outline: none;
          text-align: center;
          color: #FFFFFF;
          font-weight: 800;
          font-size: 13.5px;
          background: transparent;
          padding: 0;
        }

        .bb-bic-confirm-overlay {
          position: absolute;
          inset: 0;
          background-color: rgba(255, 255, 255, 0.96);
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          backdrop-filter: blur(2px);
          animation: fadeInConfirm 0.15s ease-out;
        }

        @keyframes fadeInConfirm {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .bb-bic-confirm-box {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .bb-bic-confirm-title {
          margin: 0;
          font-size: 13px;
          font-weight: 700;
          color: #0F172A;
        }

        .bb-bic-confirm-btn-row {
          display: flex;
          gap: 8px;
        }

        .bb-bic-confirm-cancel-btn {
          border: 1px solid #CBD5E1;
          background-color: #FFFFFF;
          color: #334155;
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }

        .bb-bic-confirm-delete-btn {
          border: none;
          background-color: #E9001E;
          color: #FFFFFF;
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        /* ── Compact Screen Adjustments (<= 360px) ── */
        @media (max-width: 360px) {
          .bb-bucket-item-card {
            padding: 10px 10px;
          }
          .bb-bic-img-wrapper {
            width: 44px;
            height: 44px;
          }
          .bb-bic-title {
            font-size: 12.5px;
          }
          .bb-bic-stepper-box {
            width: 82px;
            height: 30px;
          }
        }
      `}</style>

      {/* Top Tier: Product Thumbnail, Title/Brand/Unit, Actions */}
      <div className="bb-bic-top-tier">
        <div className="bb-bic-img-wrapper">
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={name}
              className="bb-bic-img"
              onError={() => setImgError(true)}
            />
          ) : (
            <img
              src={ToteIcon}
              alt="fallback"
              style={{ width: 22, height: 22, opacity: 0.35 }}
            />
          )}
        </div>

        <div className="bb-bic-meta-col">
          {brand && <span className="bb-bic-brand-tag">{brand}</span>}
          <h4 className="bb-bic-title" title={name}>
            {name}
          </h4>
          <span className="bb-bic-unit-spec">
            ₹{unitPrice.toLocaleString('en-IN')}{' '}
            {unitText ? `per ${unitText}` : 'per unit'}
          </span>
        </div>

        <div className="bb-bic-actions-cluster">
          <button
            className="bb-bic-action-icon-btn"
            onClick={() => {
              const target = !localIsSaved;
              setLocalIsSaved(target);
              if (onToggleSave) onToggleSave(item, target);
            }}
            type="button"
            title="Save for later"
          >
            <img
              src={localIsSaved ? FillBookmark : Bookmark}
              alt="save"
              style={{ width: 14, height: 14 }}
            />
          </button>
          <button
            className="bb-bic-action-icon-btn"
            onClick={() => setShowConfirmDelete(true)}
            type="button"
            title="Remove"
          >
            <img src={Trash} alt="remove" style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      {/* Bottom Tier: Price breakdown and Solid Bright Red Zepto Stepper */}
      <div className="bb-bic-bottom-tier">
        <div className="bb-bic-price-group">
          <span className="bb-bic-final-price">
            ₹{itemTotal.toLocaleString('en-IN')}
          </span>
          {itemMrpTotal > 0 && itemMrpTotal !== itemTotal && (
            <span className="bb-bic-mrp-price">
              ₹{itemMrpTotal.toLocaleString('en-IN')}
            </span>
          )}
        </div>

        {/* Zepto-Style Solid Red Stepper */}
        <div className="bb-bic-stepper-box">
          <button
            className="bb-bic-stepper-btn"
            onClick={handleDecrement}
            type="button"
            aria-label="Decrease quantity"
          >
            <img src={Minus} alt="-" style={{ width: 14, height: 14 }} />
          </button>

          <input
            className="bb-bic-stepper-input"
            value={inputValue}
            onChange={(e) =>
              setInputValue(e.target.value.replace(/[^0-9]/g, ''))
            }
            onBlur={handleBlur}
            maxLength={3}
            aria-label="Quantity"
          />

          <button
            className="bb-bic-stepper-btn"
            onClick={handleIncrement}
            type="button"
            aria-label="Increase quantity"
          >
            <img src={Plus} alt="+" style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Overlay */}
      {showConfirmDelete && (
        <div className="bb-bic-confirm-overlay">
          <div className="bb-bic-confirm-box">
            <h5 className="bb-bic-confirm-title">
              Remove this item from bucket?
            </h5>
            <div className="bb-bic-confirm-btn-row">
              <button
                className="bb-bic-confirm-cancel-btn"
                onClick={() => setShowConfirmDelete(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bb-bic-confirm-delete-btn"
                onClick={() => {
                  setShowConfirmDelete(false);
                  if (onRemove) onRemove(id);
                }}
                type="button"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}